import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Avatar, Checkbox, Text } from '@kula-ai/components';
import { Stars02 } from '@kula-ai/icons-core';
import { fillIdPlaceholders, matchBand } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { CandidateSummary } from '../../../types';
import { SelectionBar } from '../SelectionBar';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { CandidateTags } from './CandidateTags';
import type { CandidateBlockProps } from './CandidateBlock.types';

// Brand gradient for the AI sparkle + text; icons reference it by id (currentColor can't clip a gradient).
const SPARKLE_GRADIENT_ID = 'agentforge-sparkle-gradient';
const SPARKLE_GRADIENT_CSS = 'linear(to-br, #8A90FF, #B15FDA)';

const SparkleGradientDef = () => (
  <Box as="svg" w="0" h="0" position="absolute" aria-hidden>
    <defs>
      <linearGradient id={SPARKLE_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8A90FF" />
        <stop offset="100%" stopColor="#B15FDA" />
      </linearGradient>
    </defs>
  </Box>
);

// Selection drives the action; with nothing ticked, fall back to the whole
// list — the SAME rule for every chip, email included (product decision:
// the default action involves all listed contacts). Per-recipient email is
// safe at full-list scope because every draft opens for review — nothing
// sends on click. nameById lets {first_name}/{recipients_phrase} resolve to
// the chosen candidates' actual names.
const fillTemplate = (
  template: string,
  selectedIds: string[],
  allIds: string[],
  appIds: string[] | undefined,
  nameById: Record<string, string>
): string => {
  const perRecipientEmail = template.includes('{recipients_phrase}');
  const ids = selectedIds.length > 0 ? selectedIds : allIds;
  const names = ids.map((id) => nameById[id] ?? '');
  // Keep app ids aligned to the chosen ids (they're parallel-by-index to
  // allIds) so the application email chip carries the right application_ids.
  let scopedAppIds = appIds;
  if (perRecipientEmail && appIds) {
    const idxById = new Map(allIds.map((id, i) => [id, i]));
    scopedAppIds = ids
      .map((id) => appIds[idxById.get(id) ?? -1])
      .filter((x): x is string => typeof x === 'string');
  }
  return fillIdPlaceholders(template, ids, scopedAppIds, names);
};

// Split the match reason into a highlighted lead (first sentence) + gray remainder.
const splitReason = (reason: string): { lead: string; rest: string } => {
  const match = reason.match(/^(.*?[.!?])\s+(.+)$/s);
  if (!match) return { lead: reason, rest: '' };
  return { lead: match[1], rest: match[2] };
};

// "Head of Product · Stripe · 7yrs" — headline + years of experience.
const buildSubheadline = (c: CandidateSummary): string => {
  const parts = [c.headline].filter(Boolean);
  if (typeof c.yearsExperience === 'number' && c.yearsExperience > 0) {
    parts.push(`${c.yearsExperience}yrs`);
  }
  return parts.join(' · ');
};

const cap = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

// Human label for a matched-evidence aspect (note / scorecard / transcript / …).
const ASPECT_LABEL: Record<string, string> = {
  note: 'Note',
  scorecard: 'Scorecard',
  transcript: 'Transcript',
  interview_summary: 'Interview',
  form: 'Form',
};
const aspectLabel = (aspect: string): string =>
  ASPECT_LABEL[aspect] ?? (aspect ? cap(aspect) : 'Evidence');

// Relevance pill with the sparkle glyph; colour follows the score band.
const ScoreBadge = ({ value }: { value: number }) => {
  const pct = Math.round(value * 100);
  const band = matchBand(pct);
  return (
    <Flex
      align="center"
      gap="4px"
      flexShrink={0}
      px="8px"
      py="2px"
      borderRadius="full"
      bg={band.light}
      color={band.dark}
      fontSize="12px"
      fontWeight="600"
    >
      {/* icons-core Stars02 is a line/duotone glyph; fill the paths so the
          sparkle reads solid (matches the design). */}
      <Box as="span" display="inline-flex" lineHeight="0">
        <Stars02 size={12} className="[&_path]:fill-current" />
      </Box>
      {pct}%
    </Flex>
  );
};

const CandidateCard = ({
  candidate,
  checked,
  onToggle,
}: {
  candidate: CandidateSummary;
  checked: boolean;
  onToggle: () => void;
}) => {
  const openFloatingBridge = useAgentForgeStore((s) => s.openFloatingBridge);
  const subheadline = buildSubheadline(candidate);
  return (
    <Box
      data-test-id={`agentforge-candidate-card-${candidate.id}`}
      border="1px solid"
      borderColor={checked ? 'primary-300' : 'secondary-100'}
      borderRadius="12px"
      bg="base-white"
      p="14px"
      transition="border-color 0.15s ease"
      _hover={{ borderColor: checked ? 'primary-300' : 'secondary-200' }}
    >
      <Flex align="flex-start" justify="space-between" gap="10px">
        <Flex align="center" gap="10px" minW="0">
          <Checkbox
            size="sm"
            checked={checked}
            onChange={onToggle}
            aria-label={`Select ${candidate.name}`}
          />
          <Avatar
            src={candidate.avatarUrl}
            name={candidate.name}
            shape="square"
            size={40}
          />
          <Flex direction="column" minW="0">
            {/* Name links to the backend-supplied profile href, else plain text. */}
            {candidate.profileHref ? (
              <Box
                as={Link}
                to={candidate.profileHref}
                minW="0"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  openFloatingBridge(candidate.profileHref!);
                }}
                _hover={{ textDecoration: 'underline' }}
                data-test-id={`agentforge-candidate-profile-link-${candidate.id}`}
              >
                <Text
                  variant="h4"
                  as="span"
                  truncate
                  className="text-secondary-700"
                >
                  {candidate.name}
                </Text>
              </Box>
            ) : (
              <Text
                variant="h4"
                as="span"
                truncate
                className="text-secondary-700"
              >
                {candidate.name}
              </Text>
            )}
            {subheadline && (
              <Text variant="caption" className="text-secondary-600 truncate">
                {subheadline}
              </Text>
            )}
          </Flex>
        </Flex>
        {candidate.matchScore && (
          <ScoreBadge value={candidate.matchScore.value} />
        )}
      </Flex>

      <CandidateTags candidate={candidate} />

      {/* AI "why this matched" line. */}
      {candidate.matchReason && (
        <Flex
          align="flex-start"
          gap="8px"
          mt="10px"
          p="10px"
          borderRadius="8px"
          bg="#F0E7FE45"
        >
          <Box
            as="span"
            display="inline-flex"
            mt="2px"
            sx={{
              '& path': {
                fill: `url(#${SPARKLE_GRADIENT_ID})`,
                stroke: `url(#${SPARKLE_GRADIENT_ID})`,
              },
            }}
          >
            <Stars02 size={14} className />
          </Box>
          {(() => {
            const { lead, rest } = splitReason(candidate.matchReason);
            return (
              <Box as="span" fontSize="12px" lineHeight="1.45">
                <Box
                  as="span"
                  fontWeight="500"
                  bgGradient={SPARKLE_GRADIENT_CSS}
                  bgClip="text"
                  color="transparent"
                >
                  {lead}
                </Box>
                {rest && (
                  <Box as="span" color="secondary-700">
                    {' '}
                    {rest}
                  </Box>
                )}
              </Box>
            );
          })()}
        </Flex>
      )}

      {/* Matched-aspect evidence snippets (note / scorecard / transcript). */}
      {candidate.evidence && candidate.evidence.length > 0 && (
        <Flex direction="column" gap="6px" mt="10px">
          {candidate.evidence.map((e, i) => (
            <Flex
              key={`${e.aspect}-${i}`}
              align="flex-start"
              gap="8px"
              p="10px"
              borderRadius="8px"
              bg="secondary-50"
              border="1px solid"
              borderColor="secondary-100"
            >
              <Box
                as="span"
                flexShrink={0}
                px="6px"
                py="2px"
                borderRadius="6px"
                bg="base-white"
                border="1px solid"
                borderColor="secondary-200"
                color="secondary-700"
                fontSize="10px"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="0.03em"
              >
                {aspectLabel(e.aspect)}
                {typeof e.rating === 'number' ? ` ${e.rating}/4` : ''}
              </Box>
              <Box
                as="span"
                fontSize="12px"
                lineHeight="1.45"
                color="secondary-800"
              >
                {e.snippet}
              </Box>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
};

export const CandidateBlock = ({ data }: CandidateBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const disabled = isSending || isStreaming;

  const blockKey = data.toolCallId ?? 'no-tool-call-id';
  const selection = useAgentForgeStore((s) => s.selectionByBlock[blockKey]);
  const toggle = useAgentForgeStore((s) => s.toggleBlockSelection);
  const setSelection = useAgentForgeStore((s) => s.setBlockSelection);
  const clearSelection = useAgentForgeStore((s) => s.clearBlockSelection);
  const [showWeak, setShowWeak] = useState(false);

  const allIds = data.candidates.map((c) => c.id);
  const selectedIds = selection ?? [];
  const appIds = data.appIds ?? [];
  const nameById = Object.fromEntries(
    data.candidates.map((c) => [c.id, c.name])
  );
  const sendFollowup = (template: string) => {
    if (disabled) return;
    void send(fillTemplate(template, selectedIds, allIds, appIds, nameById));
  };

  // Append a count only when the user has explicitly CHECKED rows — a "(25)"
  // suffix on every chip when nothing is selected read as noise and implied a
  // mass action. Bare labels still act on the whole list (fillTemplate's
  // selected-else-all fallback is unchanged); the count appears the moment a
  // selection narrows the target.
  const labelFor = (label: string, template: string): string => {
    const multi = template.includes('{ids}') || template.includes('{count}');
    if (!multi || selectedIds.length === 0) return label;
    return `${label} (${selectedIds.length})`;
  };

  // Fold the weak tail behind a toggle when strongCount < result count.
  const strongCount = data.strongCount;
  const hasFold =
    typeof strongCount === 'number' && strongCount < data.candidates.length;
  const strongCandidates = hasFold
    ? data.candidates.slice(0, strongCount)
    : data.candidates;
  const weakCandidates = hasFold ? data.candidates.slice(strongCount) : [];

  const renderCard = (c: (typeof data.candidates)[number]) => (
    <CandidateCard
      key={c.id}
      candidate={c}
      checked={selectedIds.includes(c.id)}
      onToggle={() => toggle(blockKey, c.id)}
    />
  );

  return (
    <>
      <SparkleGradientDef />
      <Box data-test-id="agentforge-candidate-block">
        {/* Nexus's answer / match-warning / applied filters. */}
        {data.answer && (
          <Text
            variant="caption"
            as="p"
            className="text-secondary-800"
            data-test-id="agentforge-candidate-answer"
          >
            {data.answer}
          </Text>
        )}
        {data.matchWarning && (
          <Box
            mt="6px"
            px="10px"
            py="6px"
            borderRadius="8px"
            bg="pastel-6-light"
            color="pastel-6-dark"
            fontSize="12px"
            fontWeight="500"
            data-test-id="agentforge-candidate-match-warning"
          >
            {data.matchWarning === 'no_exact_matches'
              ? 'No exact match for every requirement — showing the closest candidates.'
              : 'No strong match — these are the closest; review carefully before acting.'}
          </Box>
        )}
        {data.appliedFilterChips && data.appliedFilterChips.length > 0 && (
          <Flex wrap="wrap" gap="6px" mt="8px">
            {data.appliedFilterChips.map((chip) => (
              <Box
                key={chip}
                px="8px"
                py="2px"
                borderRadius="6px"
                bg="secondary-50"
                color="secondary-700"
                fontSize="11px"
                fontWeight="500"
              >
                {chip}
              </Box>
            ))}
          </Flex>
        )}
        <Box mt="10px">
          <SelectionBar
            selected={selectedIds.length}
            total={allIds.length}
            noun="candidates"
            onSelectAll={() => setSelection(blockKey, allIds)}
            onClear={() => clearSelection(blockKey)}
            testId="agentforge-candidate-selection"
          />
        </Box>
        <Flex direction="column" gap="10px" mt="10px">
          {strongCandidates.map(renderCard)}
        </Flex>

        {hasFold && (
          <Box mt="10px">
            <Box
              as="button"
              type="button"
              onClick={() => setShowWeak((v) => !v)}
              px="12px"
              py="6px"
              borderRadius="8px"
              border="1px solid"
              borderColor="border-color"
              bg="base-white"
              color="secondary-700"
              fontSize="12px"
              fontWeight="500"
              cursor="pointer"
              _hover={{ bg: 'secondary-50' }}
              data-test-id="agentforge-candidate-toggle-weak"
            >
              {showWeak
                ? 'Hide weaker matches'
                : `Show ${weakCandidates.length} weaker ${
                    weakCandidates.length === 1 ? 'match' : 'matches'
                  }`}
            </Box>
            {showWeak && (
              <Flex direction="column" gap="10px" mt="10px">
                {weakCandidates.map(renderCard)}
              </Flex>
            )}
          </Box>
        )}

        {/* Page more rows from nexus's cached set via kula_search(cache_key, offset). */}
        {data.loadMore && (
          <Box mt="10px">
            <Box
              as="button"
              type="button"
              disabled={disabled}
              opacity={disabled ? 0.5 : 1}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              onClick={() => {
                if (disabled || !data.loadMore) return;
                void send(
                  `Load the next page of the previous search (cache_key: ${data.loadMore.cacheKey}, offset: ${data.loadMore.offset}).`
                );
              }}
              px="12px"
              py="6px"
              borderRadius="8px"
              border="1px solid"
              borderColor="border-color"
              bg="base-white"
              color="secondary-700"
              fontSize="12px"
              fontWeight="500"
              _hover={{ bg: 'secondary-50' }}
              data-test-id="agentforge-candidate-load-more"
            >
              <Flex as="span" align="center" gap="6px">
                {disabled && <Spinner size="xs" color="primary.500" />}
                {typeof data.loadMore.remaining === 'number'
                  ? `Show ${data.loadMore.remaining} more`
                  : 'Show more'}
              </Flex>
            </Box>
          </Box>
        )}

        {data.suggestedActions && data.suggestedActions.length > 0 && (
          <Box mt="14px">
            <SuggestedActionChips
              actions={data.suggestedActions.map((a) => ({
                ...a,
                label: labelFor(a.label, a.promptTemplate),
              }))}
              onSelect={sendFollowup}
              disabled={disabled}
              testIdPrefix="agentforge-candidate-suggested"
            />
          </Box>
        )}
      </Box>
    </>
  );
};
